import { TicketRefundAdminPage, TicketRefundPublicPage } from "../features/ticket-refund";
import { useEffect, useState, type ReactNode } from "react";
import { Navigate, Outlet, createBrowserRouter, createMemoryRouter, useLocation, useParams } from "react-router-dom";

import { DatabaseConnectivityDemoPage } from "../features/demo/pages/SqlServerDemoPage";
import { MySqlQueryListPage } from "../features/demo/pages/MySqlQueryListPage";
import { MySqlQueryListDemo2Page } from "../features/demo/pages/MySqlQueryListDemo2Page";
import { LoginPage } from "../features/auth/pages/LoginPage";
import { LogoutPage } from "../features/auth/pages/LogoutPage";
import { ResetPasswordPage } from "../features/auth/pages/ResetPasswordPage";
import { LegacySingleLoginGate, PermissionRoute, useCurrentAuthUser } from "../features/auth";
import { ApprovalInboxPage, ApprovalSettingsPage } from "../features/approval";
import { AuthzRoleDetailPage, AuthzRoleListPage } from "../features/authz_management";
import { CustomizationProjectListPage, CustomizationWorkbenchOpeningPage, CustomizationWorkbenchPage, CustomizationDemandExcelDetailPage } from "../features/customization";
import {
  CustomizationMobileTracePage,
  CustomizationMobileItineraryPage,
  CustomizationMobileWorkbenchPage,
  CustomizationMobileWorkbenchShell,
} from "../features/customization-mobile-workbench";
import { AdminLayout } from "./layouts/AdminLayout";
import { AiContestEnrollmentPage } from "../features/ai-contest-enrollment";
import { WorkspacePage } from "../features/workspace/pages/WorkspacePage";
import { AdminPlaceholderPage } from "./layouts/AdminPlaceholderPage";
import { adminNavigationConfig, filterAdminNavigationConfig } from "./layouts/admin.config";
import { ChromaVectorSyncPage } from "../features/chroma_vector_sync";
import { CrmCollaborationTaskPage } from "../features/crm_collaboration_task";
import { CrmContactWorkbenchPage } from "../features/crm_contact_workbench";
import { CrmContactImportPage } from "../features/crm_contact_import";
import { CrmContactMergePage } from "../features/crm_contact_merge";
import { CrmCustomerWorkbenchPage } from "../features/crm_customer_workbench";
import { CrmMarketRegionPage } from "../features/crm_market_region";
import {
  CrmOpportunityWorkbenchPage,
  loadCrmOpportunitySplitPage,
} from "../features/crm_opportunity_workbench";
import { CrmOrganizationPage } from "../features/crm_organization";
import { CrmOrganizationUnitPage } from "../features/crm_organization_unit";
import { DicMajorPage } from "../features/dic_major";
import { BusVehicleTypePage } from "../features/bus-vehicle-type";
import { DicStayAreaPage } from "../features/dic_stay_area";
import { LegacySyncPage, LegacySyncTestPage } from "../features/legacy_sync";
import { ItineraryListPage, ItineraryMySharesPage } from "../features/itinerary";
import { ITINERARY_PERMISSIONS } from "../features/itinerary";
import {
  OrderHandoverContractSignPage,
  OrderHandoverDetailPage,
  OrderHandoverRosterImportPage,
  OrderHandoverMemberFeeAdjustmentPage,
} from "../features/order-handover";
import { CustomerQuoteBudgetReadonlyPage, CustomerQuoteEntryRedirectPage, CustomerQuotePage } from "../features/customer-quote";
import {
  FulfillmentOrderEditorPage,
  FulfillmentOrderEntryRedirectPage,
  FulfillmentOrderItineraryPage,
  FulfillmentOrderListPage,
} from "../features/fulfillment-order";
import { QuoteBudgetEditorPage, QuoteBudgetEntryRedirectPage, QuoteBudgetVersionComparePage } from "../features/quote_budget";
import { ResourceProfileRebuildPage } from "../features/resource_profile_rebuild";
import { ResourcePointManagementPage } from "../features/resource-point-management/pages/ResourcePointManagementPage";
import { ResourcePointMergePage } from "../features/resource-point-management/pages/ResourcePointMergePage";
import { PaymentValidationPage } from "../features/payment-validation";
import {
  FinancePayableListPage,
  FinancePayableOverviewPage,
  FinancePaymentRequestDetailPage,
  FinancePaymentRequestListPage,
  FinanceReceivableListPage,
  FinanceReceivableOverviewPage,
  FinanceWorkbenchPage,
} from "../features/finance";
import { StayResourceManagementPage } from "../features/stay_resource_management";
import {
  BusesPage,
  GroundsPage,
  GuidesPage,
  HotelsPage,
  RestaurantsPage,
} from "../features/supply-basic-settings";
import { SysDeptUserPage } from "../features/sys_dept_user";
import { ItineraryEditorPage, ItineraryEntryRedirectPage, ItineraryPdfRenderPage, ItineraryPreviewPage, ItineraryPublicPdfRenderPage, ItineraryPublicSharePage, ItineraryTablePage } from "../features/itinerary";
import { HotelMapPage } from "../features/hotel-map";
import { CrmLegacySyncPage } from "../features/crm_legacy_sync";
import { SalesPerformancePage } from "../features/sales-performance";
import { GeoGovernancePage } from "../features/geo_governance";
import { NotificationCenterPage } from "../features/notification-center";
import { BusinessPageTitleContext, type BusinessPageTitle } from "../lib/navigation/useBusinessPageTitle";

/** 铁路独立页面与后台页面共用同一业务出口和权限守卫。 */
const loadRailwayQueryRoute = async () => {
  const { RailwayQueryPage } = await import("../features/railway-query");
  return { Component: () => protectPage("railway.query:access", <RailwayQueryPage />) };
};

const placeholderRoutes = adminNavigationConfig
  .filter(
    (item) =>
      ![
        "/workspace",
        "/demo/sqlserver",
        "/demo/mysql-query-list",
        "/demo/mysql-query-list-demo2",
        "/payment-validation/collections",
        "/crm/customization/projects",
        "/crm/customization/projects/project-demo/workbench",
        "/crm/customization-mobile/projects/project-demo/workbench",
        "/crm/customization-mobile/projects/project-demo/traces",
        "/crm/order-handover",
        "/crm/order-handover/project-demo",
        "/crm/order-handover/project-demo/roster/import",
        "/crm/collaboration/tasks",
        "/crm/collaboration/pools",
        "/crm/contacts/workbench",
        "/crm/contacts/import",
        "/crm/contacts/merge",
        "/crm/customers/workbench",
        "/crm/opportunities/workbench",
        "/crm/market-regions",
        "/crm/collaboration/pools/tele-sales",
        "/crm/collaboration/pools/strong-follow",
        "/crm/collaboration/my-items",
        "/dic/majors",
        "/dic/stay-areas",
        "/data-governance/chroma-vector-sync",
        "/data-governance/legacy-sync",
        "/data-governance/crm-legacy-sync",
        "/data-governance/legacy-sync-test",
        "/data-governance/resource-profile-rebuild",
        "/data-governance/geo",
        "/resources/points",
        "/resources/points/enterprise",
        "/resources/points/intangible",
        "/resources/points/other",
        "/resources/points/railway",
        "/railway-query",
        "/resources/points/airport",
        "/resources/stays",
        "/resources/stay-map",
        "/auth/reset-password",
        "/auth/logout",
        "/notifications/inbox",
        "/sys/dept-users",
        "/settings/org-workflow/roles",
        "/approval/settings",
        "/approval/todo",
        "/crm/school-academy-major/school-organizations",
        "/crm/school-academy-major/organization-units",
        "/product-lines/course-routes",
        "/itineraries/my-shares",
        "/supply-chain",
        "/supply-chain/fulfillment-orders",
        "/supply-chain/hotel-map",
        "/supply-chain/basic-settings",
        "/supply-chain/basic-settings/guides",
        "/supply-chain/basic-settings/buses",
        "/supply-chain/basic-settings/restaurants",
        "/supply-chain/basic-settings/hotels",
        "/supply-chain/basic-settings/grounds",
        "/finance",
        "/finance/workbench",
        "/finance/payable-list",
        "/finance/payables",
        "/finance/receivable-list",
        "/finance/receivables",
        "/finance/payment-requests",
      ].includes(item.path),
  )
  .map((item) => ({
    path: item.path.slice(1),
    element: <AdminPlaceholderPage />,
  }));

const protectPage = (permission: string | string[], element: ReactNode) => {
  const permissions = Array.isArray(permission) ? permission : [permission];
  return permissions.reduceRight<ReactNode>(
    (child, requiredPermission) => (
      <PermissionRoute permission={requiredPermission}>{child}</PermissionRoute>
    ),
    element,
  );
};

/**
 * 财务结算系统入口：跳到当前账号能看的第一个子页，顺序同侧栏菜单。
 * 一个都不能看时仍进应付清单，由它的页面守卫给出无权访问或登录失效。
 */
function FinanceIndexRedirect() {
  const currentUserQuery = useCurrentAuthUser();
  if (currentUserQuery.isPending || currentUserQuery.isLoading) return null;
  const target = filterAdminNavigationConfig(adminNavigationConfig, currentUserQuery.data).find((item) =>
    item.parentKeys?.includes("finance"),
  );
  return <Navigate to={target?.path ?? "/finance/payables"} replace />;
}

/** 确认件编辑独立入口仅承接业务标题，不挂载后台母版与页签。 */
function StandaloneFulfillmentRoute() {
  const { pathname } = useLocation();
  const [businessTitle, setBusinessTitle] = useState<BusinessPageTitle | null>(null);
  const title = businessTitle?.pathname === pathname ? businessTitle.title : "确认件编辑";
  useEffect(() => {
    const previousTitle = document.title;
    const currentTitle = `${title} | 花儿研学`;
    document.title = currentTitle;
    return () => { if (document.title === currentTitle) document.title = previousTitle; };
  }, [title]);
  return <BusinessPageTitleContext.Provider value={setBusinessTitle}><Outlet /></BusinessPageTitleContext.Provider>;
}

const appRoutes = [
  { path: "/ticket-refunds/public/:token", element: <TicketRefundPublicPage /> },
  { path: "/login", element: <Navigate to="/auth/login" replace /> },
  { path: "/auth/login", element: <LoginPage /> },
  { path: "/auth/register", element: <LoginPage initialMode="register" /> },
  { path: "/railway-query/view", lazy: loadRailwayQueryRoute },
  { path: "/itineraries/share/:token", element: <ItineraryPublicSharePage /> },
  { path: "/itineraries/:itineraryId/table", element: protectPage([ITINERARY_PERMISSIONS.access, ITINERARY_PERMISSIONS.previewAccess, ITINERARY_PERMISSIONS.table], <ItineraryTablePage />) },
  { path: "/contest/enrollment", element: <AiContestEnrollmentPage /> },
  { path: "/itineraries/public/:token/pdf-render", element: <ItineraryPublicPdfRenderPage /> },
  {
    path: "/itineraries/:itineraryId/pdf-render",
    element: protectPage([ITINERARY_PERMISSIONS.access, ITINERARY_PERMISSIONS.previewAccess, ITINERARY_PERMISSIONS.export], <ItineraryPdfRenderPage />),
  },
  {
    path: "/itineraries/demands/:demandId/entry",
    element: protectPage(ITINERARY_PERMISSIONS.create, <ItineraryEntryRedirectPage />),
  },
  {
    path: "/itineraries/:itineraryId/edit",
    element: protectPage(ITINERARY_PERMISSIONS.editorAccess, <ItineraryEditorPage />),
  },
  {
    path: "/itineraries/:itineraryId/preview",
    element: protectPage([ITINERARY_PERMISSIONS.access, ITINERARY_PERMISSIONS.previewAccess], <ItineraryPreviewPage />),
  },
  {
    path: "/quote/budgets/:budgetOrderId/compare",
    element: protectPage("opportunity.quote_budget:access", <QuoteBudgetVersionComparePage />),
  },
  {
    path: "/crm/customization/projects/:projectId/demands/:demandId/excel-detail",
    element: protectPage(
      ["customization.project:access", "customization.project:preview"],
      <CustomizationDemandExcelDetailPage />,
    ),
  },
  {
    path: "/order-handover/contracts/sign-portal/:signRecordId",
    element: <OrderHandoverContractSignPage />,
  },
  {
    path: "/crm/order-handover/contracts/sign/:signRecordId",
    element: <OrderHandoverContractSignPage />,
  },
  {
    path: "/crm/order-handover/:projectId/roster/import",
    element: protectPage(
      "customization.order_handover:access",
      <OrderHandoverRosterImportPage />,
    ),
  },
  {
    path: "/crm/order-handover/:projectId/rosters/:rosterId/members/:memberId/fee-adjustments",
    element: protectPage("customization.order_handover:access", <OrderHandoverMemberFeeAdjustmentPage />),
  },
  {
    element: <LegacySingleLoginGate><StandaloneFulfillmentRoute /></LegacySingleLoginGate>,
    children: [
      {
        path: "/supply-chain/fulfillment-orders/itineraries/:itineraryId",
        element: protectPage(["fulfillment.order:create", "fulfillment.order:access"], <FulfillmentOrderItineraryPage />),
      },
      {
        path: "/supply-chain/fulfillment-orders/itineraries/:itineraryId/enter",
        element: protectPage(["fulfillment.order:create", "fulfillment.order:access"], <FulfillmentOrderEntryRedirectPage />),
      },
      {
        path: "/supply-chain/fulfillment-orders/workspaces/:workspaceId",
        element: protectPage("fulfillment.order:access", <FulfillmentOrderEditorPage />),
      },
    ],
  },
  {
    path: "/",
    element: (
      <LegacySingleLoginGate>
        <AdminLayout />
      </LegacySingleLoginGate>
    ),
    children: [
      { index: true, element: <Navigate to="/workspace" replace /> },
      { path: "workspace", element: protectPage("workspace.dashboard:access", <WorkspacePage />) },
      { path: "railway-query", lazy: loadRailwayQueryRoute },
      { path: "demo/sqlserver", element: <DatabaseConnectivityDemoPage /> },
      { path: "demo/mysql/query-list", element: <Navigate to="/demo/mysql-query-list" replace /> },
      { path: "demo/mysql/query-list-demo2", element: <Navigate to="/demo/mysql-query-list-demo2" replace /> },
      { path: "crm/customization/projects", element: protectPage("customization.project:list", <CustomizationProjectListPage />) },
      { path: "crm/collaboration/tasks", element: protectPage("crm.collaboration:access", <CrmCollaborationTaskPage />) },
      { path: "crm/collaboration/pools", element: <Navigate to="/crm/collaboration/pools/tele-sales" replace /> },
      { path: "crm/contacts/workbench", element: protectPage("crm.contact:access", <CrmContactWorkbenchPage />) },
      { path: "crm/contacts/import", element: protectPage("crm.contact_import:access", <CrmContactImportPage />) },
      { path: "crm/contacts/merge", element: protectPage("crm.contact_merge:access", <CrmContactMergePage />) },
      { path: "crm/customers/workbench", element: protectPage("crm.customer:access", <CrmCustomerWorkbenchPage />) },
      { path: "crm/opportunities/workbench", element: protectPage("crm.opportunity:access", <CrmOpportunityWorkbenchPage />) },
      {
        path: "crm/opportunities/:opportunityId/split",
        lazy: async () => {
          const { CrmOpportunitySplitPage } = await loadCrmOpportunitySplitPage();
          return {
            Component: () => protectPage("crm.opportunity:access", <CrmOpportunitySplitPage />),
          };
        },
      },
      { path: "crm/market-regions", element: protectPage("crm.region_config:access", <CrmMarketRegionPage />) },
      {
        path: "crm/collaboration/pools/tele-sales",
        element: protectPage("crm.collaboration:access", <CrmCollaborationTaskPage initialTab="tele_sales" />),
      },
      {
        path: "crm/collaboration/pools/strong-follow",
        element: protectPage("crm.collaboration:access", <CrmCollaborationTaskPage initialTab="strong_follow" />),
      },
      { path: "crm/collaboration/my-items", element: protectPage("crm.collaboration:access", <CrmCollaborationTaskPage initialTab="my_items" />) },
      {
        path: "crm/customization/:projectId/workbench",
        element: <LegacyCustomizationWorkbenchRedirect />,
      },
      { path: "crm/customization/projects/new/workbench", element: protectPage(["customization.project:access", "customization.project:workbench"], <CustomizationWorkbenchPage projectId="new" />) },
      {
        path: "crm/customization/projects/opening",
        element: protectPage(["customization.project:access", "customization.project:workbench"], <CustomizationWorkbenchOpeningPage />),
      },
      {
        path: "crm/opportunities/:opportunityId/customization-projects/:projectId/workbench",
        element: protectPage(
          ["crm.opportunity:access", "customization.project:access", "customization.project:workbench"],
          <CustomizationWorkbenchPage />,
        ),
      },
      { path: "crm/customization/projects/:projectId/workbench", element: protectPage(["customization.project:access", "customization.project:workbench"], <CustomizationWorkbenchPage />) },
      {
        path: "crm/customization-mobile/projects/:projectId",
        element: protectPage(["customization.project:access", "customization.project:workbench"], <CustomizationMobileWorkbenchShell />),
        children: [
          { path: "workbench", element: <CustomizationMobileWorkbenchPage /> },
          { path: "traces", element: <CustomizationMobileTracePage /> },
          { path: "itinerary", element: <CustomizationMobileItineraryPage /> },
        ],
      },
      { path: "crm/order-handover", element: <Navigate to="/crm/customization/projects" replace /> },
      { path: "crm/order-handover/:projectId", element: protectPage("customization.order_handover:access", <OrderHandoverDetailPage />) },
      { path: "demo/mysql-query-list", element: <MySqlQueryListPage /> },
      { path: "demo/mysql-query-list-demo2", element: <MySqlQueryListDemo2Page /> },
      {
        path: "payment-validation/collections",
        element: protectPage(
          "payment_validation.collection:access",
          <PaymentValidationPage />,
        ),
      },
      { path: "dic/majors", element: protectPage("dictionary.major:access", <DicMajorPage />) },
      { path: "dic/bus-vehicle-types", element: protectPage("dictionary.bus_vehicle_type:access", <BusVehicleTypePage />) },
      { path: "dic/stay-areas", element: protectPage("dictionary.stay_area:access", <DicStayAreaPage />) },
      { path: "data-governance/chroma-vector-sync", element: protectPage("data.chroma_vector:access", <ChromaVectorSyncPage />) },
      { path: "data-governance/legacy-sync", element: protectPage("data.legacy_sync:access", <LegacySyncPage />) },
      { path: "data-governance/crm-legacy-sync", element: protectPage("data.crm_legacy_sync:access", <CrmLegacySyncPage />) },
      { path: "sales-performance", element: protectPage("sales_performance.report:access", <SalesPerformancePage />) },
      { path: "data-governance/legacy-sync-test", element: protectPage("data.legacy_sync:access", <LegacySyncTestPage />) },
      { path: "data-governance/resource-profile-rebuild", element: protectPage("data.resource_profile:access", <ResourceProfileRebuildPage />) },
      { path: "data-governance/geo", element: protectPage("data.geo_governance:access", <GeoGovernancePage />) },
      {
        path: "resources/points",
        element: <ResourcePointsEntryPage />,
      },
      {
        path: "resources/points/merge",
        element: protectPage("resource.point_merge:access", <ResourcePointMergePage />),
      },
      {
        path: "resources/points/enterprise",
        element: protectPage(
          "resource.enterprise:access",
          <ResourcePointManagementPage scope="enterprise" />,
        ),
      },
      {
        path: "resources/points/intangible",
        element: protectPage(
          "resource.intangible:access",
          <ResourcePointManagementPage scope="intangible" />,
        ),
      },
      {
        path: "resources/points/other",
        element: protectPage(
          "resource.point:access",
          <ResourcePointManagementPage scope="other" />,
        ),
      },
      {
        path: "resources/points/railway",
        element: protectPage(
          "resource.railway_station:access",
          <ResourcePointManagementPage scope="railway" />,
        ),
      },
      {
        path: "resources/points/airport",
        element: protectPage(
          "resource.airport:access",
          <ResourcePointManagementPage scope="airport" />,
        ),
      },
      {
        path: "resources/points/station",
        element: <Navigate to="/resources/points/railway" replace />,
      },
      { path: "resources/stays", element: protectPage("resource.stay:access", <StayResourceManagementPage />) },
      { path: "resources/stays/:spuId", element: protectPage("resource.stay:access", <StayResourceManagementPage />) },
      { path: "resources/stay-map", element: protectPage("resource.stay_map:access", <HotelMapPage />) },
      { path: "product-lines/course-routes", element: protectPage(ITINERARY_PERMISSIONS.access, <ItineraryListPage />) },
      { path: "ticket-refunds/teams/:teamId", element: protectPage("order_handover.ticket_refund:access", <TicketRefundAdminPage />) },
      { path: "itineraries/my-shares", element: protectPage(ITINERARY_PERMISSIONS.mySharesAccess, <ItineraryMySharesPage />) },
      { path: "auth/reset-password", element: <ResetPasswordPage /> },
      { path: "auth/logout", element: <LogoutPage /> },
      { path: "notifications/inbox", element: <NotificationCenterPage /> },
      { path: "sys/dept-users", element: protectPage("system.dept_user:access", <SysDeptUserPage />) },
      { path: "approval/settings", element: protectPage("approval.flow:access", <ApprovalSettingsPage />) },
      // 待我审批不挂权限码：审批人在审批设置里按人指定，登录即可进入，看到哪些单子由后端按当前审批人判断。
      { path: "approval/todo", element: <ApprovalInboxPage /> },
      {
        path: "settings/org-workflow/roles",
        element: (
          <PermissionRoute permission="system.role:access">
            <AuthzRoleListPage />
          </PermissionRoute>
        ),
      },
      {
        path: "settings/org-workflow/roles/:roleId",
        element: (
          <PermissionRoute permission="system.role:access">
            <AuthzRoleDetailPage />
          </PermissionRoute>
        ),
      },
      {
        path: "quote/itineraries/:itineraryId/budget",
        element: protectPage("opportunity.quote_budget:create", <QuoteBudgetEntryRedirectPage />),
      },
      {
        path: "quote/budgets/:budgetOrderId",
        element: protectPage("opportunity.quote_budget:access", <QuoteBudgetEditorPage />),
      },
      {
        path: "quote/budgets/:budgetOrderId/customer-quote",
        element: protectPage(
          "opportunity.customer_quote:create",
          protectPage("opportunity.customer_quote:access", <CustomerQuoteEntryRedirectPage />),
        ),
      },
      {
        path: "quote/customer-quotes/:customerQuoteId",
        element: protectPage("opportunity.customer_quote:access", <CustomerQuotePage />),
      },
      {
        path: "quote/customer-quotes/:customerQuoteId/budget",
        element: protectPage("opportunity.customer_quote:access", protectPage("opportunity.quote_budget:access", <CustomerQuoteBudgetReadonlyPage />)),
      },
      { path: "supply-chain", element: <Navigate to="/supply-chain/basic-settings" replace /> },
      { path: "supply-chain/fulfillment-orders", element: protectPage("fulfillment.order:access", <FulfillmentOrderListPage />) },
      { path: "supply-chain/hotel-map", element: <Navigate to="/resources/stay-map" replace /> },
      { path: "supply-chain/basic-settings", element: <Navigate to="/supply-chain/basic-settings/guides" replace /> },
      { path: "supply-chain/basic-settings/guides", element: protectPage("supply.basic_settings:access", <GuidesPage />) },
      { path: "supply-chain/basic-settings/buses", element: protectPage("supply.basic_settings:access", <BusesPage />) },
      { path: "supply-chain/basic-settings/restaurants", element: protectPage("supply.basic_settings:access", <RestaurantsPage />) },
      { path: "supply-chain/basic-settings/hotels", element: protectPage("supply.basic_settings:access", <HotelsPage />) },
      { path: "supply-chain/basic-settings/grounds", element: protectPage("supply.basic_settings:access", <GroundsPage />) },
      { path: "finance", element: <FinanceIndexRedirect /> },
      { path: "finance/workbench", element: protectPage("finance.workbench:access", <FinanceWorkbenchPage />) },
      {
        path: "finance/payable-list",
        element: protectPage("finance.payable_list:access", <FinancePayableOverviewPage />),
      },
      { path: "finance/payables", element: protectPage("fulfillment.order:access", <FinancePayableListPage />) },
      {
        path: "finance/receivable-list",
        element: protectPage("finance.receivable_list:access", <FinanceReceivableOverviewPage />),
      },
      {
        path: "finance/receivables",
        element: protectPage("customization.order_handover:access", <FinanceReceivableListPage />),
      },
      {
        path: "finance/payment-requests",
        element: protectPage("finance.payment_request:access", <FinancePaymentRequestListPage />),
      },
      // 请款详情不挂权限码：审批人从待我审批点「查看请款单」打开，他不一定有看请款记录的权限，能不能看由后端判断（有看请款记录的权限，或是这张请款的审批人）。
      { path: "finance/payment-requests/:requestId", element: <FinancePaymentRequestDetailPage /> },
      {
        path: "crm/school-academy-major/school-organizations",
        element: protectPage("system.organization:access", <CrmOrganizationPage />),
      },
      {
        path: "crm/school-academy-major/organization-units",
        element: protectPage("system.organization:access", <CrmOrganizationUnitPage />),
      },
      {
        path: "settings/school-academy-major/school-organizations",
        element: <LegacySchoolAcademyMajorRedirect target="/crm/school-academy-major/school-organizations" />,
      },
      {
        path: "settings/school-academy-major/organization-units",
        element: <LegacySchoolAcademyMajorRedirect target="/crm/school-academy-major/organization-units" />,
      },
      ...placeholderRoutes,
    ],
  },
];

/**
 * 构建应用路由配置。
 */
export function buildAppRouter(initialEntries?: string[]) {
  if (initialEntries) {
    return createMemoryRouter(appRoutes, { initialEntries });
  }

  return createBrowserRouter(appRoutes);
}

/** 将旧工作台路由迁移到项目列表子路由。 */
function LegacyCustomizationWorkbenchRedirect() {
  const { projectId } = useParams();
  return <Navigate to={`/crm/customization/projects/${projectId ?? "project-demo"}/workbench`} replace />;
}

/** 旧「系统管理 / 基础设置」校院系专业路径迁移到 CRM，并保留查询串。 */
function LegacySchoolAcademyMajorRedirect({ target }: { target: string }) {
  const location = useLocation();
  return <Navigate to={`${target}${location.search}${location.hash}`} replace />;
}

/** 旧考察点入口：GEO 深链保留全量页，其余跳转新的普通考察点菜单。 */
function ResourcePointsEntryPage() {
  const location = useLocation();
  const user = useCurrentAuthUser().data;
  const visible = filterAdminNavigationConfig(adminNavigationConfig, user)
    .filter((item) => item.key.startsWith("resource-points-"));
  const entry = visible.find((item) => item.key === "resource-points-other") ?? visible[0];
  const geoEditId = new URLSearchParams(location.search).get("geoEditId");
  return (
    <PermissionRoute permission={entry?.requiredPermission ?? "resource.point:access"}>
      {geoEditId
        ? <ResourcePointManagementPage scope="all" />
        : <Navigate to={entry?.path ?? "/resources/points/other"} replace />}
    </PermissionRoute>
  );
}
